import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search,
  Loader2,
  Copy,
  Check,
  Home,
  Bath,
  Bed,
  Ruler,
  ExternalLink,
  AlertCircle,
  ImageOff,
  MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export default function PropertyLookupPage() {
  const { api } = useAuth();
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const suggestAbortRef = useRef(null);
  const detailsAbortRef = useRef(null);
  const retryTimerRef = useRef(null);
  
  // Input state
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  // Selected address state
  const [selectedAddress, setSelectedAddress] = useState(null);
  
  // Lookup state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Debounce query for suggestions (300ms - balances feel vs API load)
  const debouncedQuery = useDebounce(query, 300);
  
  // Fetch suggestions when debounced query changes - with cancellation
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!debouncedQuery || debouncedQuery.length < 3) {
        setSuggestions([]);
        return;
      }
      
      // Skip if user already selected this exact address
      if (selectedAddress && selectedAddress.display === debouncedQuery) {
        return;
      }
      
      // Cancel any in-flight suggest request (prevents stale responses)
      if (suggestAbortRef.current) {
        suggestAbortRef.current.abort();
      }
      const controller = new AbortController();
      suggestAbortRef.current = controller;
      
      setLoadingSuggestions(true);
      try {
        const response = await api().get(
          `/property-lookup/suggest?q=${encodeURIComponent(debouncedQuery)}`,
          { signal: controller.signal }
        );
        // Only apply if this is still the current request
        if (suggestAbortRef.current === controller) {
          setSuggestions(response.data.suggestions || []);
          setShowSuggestions(true);
        }
      } catch (error) {
        if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
          console.error('Suggestion error:', error);
          if (suggestAbortRef.current === controller) {
            setSuggestions([]);
          }
        }
      } finally {
        if (suggestAbortRef.current === controller) {
          setLoadingSuggestions(false);
        }
      }
    };
    
    fetchSuggestions();
  }, [debouncedQuery, api, selectedAddress]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (suggestAbortRef.current) suggestAbortRef.current.abort();
      if (detailsAbortRef.current) detailsAbortRef.current.abort();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Fetch property details (separated for reuse / retry)
  const fetchDetails = useCallback(async (suggestion, { showSpinner = true } = {}) => {
    if (detailsAbortRef.current) detailsAbortRef.current.abort();
    const controller = new AbortController();
    detailsAbortRef.current = controller;
    
    if (showSpinner) setLoading(true);
    try {
      const response = await api().post('/property-lookup/details', suggestion, {
        signal: controller.signal,
      });
      if (detailsAbortRef.current === controller) {
        setResult(response.data);
        return response.data;
      }
    } catch (error) {
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        console.error('Lookup error:', error);
        if (detailsAbortRef.current === controller) {
          setResult({
            status: 'fetch_failed',
            address_input: suggestion.display,
            matched_address: suggestion.display,
          });
        }
      }
    } finally {
      if (detailsAbortRef.current === controller && showSpinner) {
        setLoading(false);
      }
    }
    return null;
  }, [api]);
  
  // Handle selecting a suggestion - instant UI update, non-blocking details fetch
  const handleSelectSuggestion = useCallback(async (suggestion) => {
    // 1. INSTANT UI: populate field, close dropdown, show selected address
    setSelectedAddress(suggestion);
    setQuery(suggestion.display);
    setShowSuggestions(false);
    setSuggestions([]);
    setResult(null);
    
    // Clear any pending retry timer
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    
    // 2. GUARD: if user's original query had a house number but this suggestion doesn't,
    //    do NOT proceed to /details - show incomplete-address state instead.
    const typedNumMatch = query.trim().match(/^\s*(\d+[a-z]?)\b/i);
    const typedNum = typedNumMatch ? typedNumMatch[1] : null;
    const suggestionHasNumber = Boolean(suggestion.street_number);
    
    if (typedNum && !suggestionHasNumber) {
      // Road-only result while user asked for a specific house number.
      // Don't pretend this is an exact property match.
      setResult({
        status: 'incomplete_address',
        address_input: suggestion.display,
        matched_address: suggestion.display,
        domain_search_url: `https://www.domain.com.au/sold-listings/?searchterm=${encodeURIComponent(typedNum + ' ' + suggestion.display)}`,
        realestate_search_url: `https://www.realestate.com.au/property/?searchTerm=${encodeURIComponent(typedNum + ' ' + suggestion.display)}`,
        google_search_url: `https://www.google.com/search?q=${encodeURIComponent(typedNum + ' ' + suggestion.display)}+site%3Adomain.com.au+OR+site%3Arealestate.com.au`,
      });
      toast.info('This is a road-level match. Refine the address for an exact lookup.');
      return;
    }
    
    // 3. Kick off details fetch (non-blocking for UI - loading state handles it)
    const data = await fetchDetails(suggestion);
    
    // 4. If backend returned status='pending' (scraping in background),
    //    auto-retry once after 4s to get the real result from cache
    if (data && data.status === 'pending') {
      retryTimerRef.current = setTimeout(() => {
        fetchDetails(suggestion, { showSpinner: false });
        retryTimerRef.current = null;
      }, 4000);
    }
  }, [fetchDetails, query]);
  
  // Handle form submit (for manual entry without selecting suggestion)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    
    // If we have suggestions, use the first one
    if (suggestions.length > 0 && !selectedAddress) {
      handleSelectSuggestion(suggestions[0]);
      return;
    }
    
    // If already have a selected address, re-lookup (re-uses fetchDetails)
    if (selectedAddress) {
      setResult(null);
      const data = await fetchDetails(selectedAddress);
      if (data && data.status === 'pending') {
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          fetchDetails(selectedAddress, { showSpinner: false });
          retryTimerRef.current = null;
        }, 4000);
      }
      return;
    }
    
    // Fallback to legacy endpoint
    setLoading(true);
    setResult(null);
    try {
      const response = await api().post('/property-lookup', { address: query.trim() });
      setResult(response.data);
    } catch (error) {
      toast.error('Failed to lookup property');
      setResult({ status: 'fetch_failed', address_input: query });
    } finally {
      setLoading(false);
    }
  };
  
  // Clear selection when user types
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    // Clear selection if user is typing something different
    if (selectedAddress && value !== selectedAddress.display) {
      setSelectedAddress(null);
      setResult(null);
    }
    
    // Show suggestions dropdown
    if (value.length >= 3) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };
  
  // Copy to clipboard
  const copyToClipboard = async () => {
    if (!result || result.status !== 'success') return;
    
    const text = `Address: ${result.matched_address || result.address_input}
Bedrooms: ${result.bedrooms ?? 'Not available'}
Bathrooms: ${result.bathrooms ?? 'Not available'}
House Size: ${result.house_size ? `${result.house_size} ${result.house_size_unit || 'm²'}` : 'Not available'}
Source: ${result.source_url || 'Not available'}`;
    
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy — check browser permissions');
    }
  };
  
  // Status message (high-level)
  const getStatusMessage = () => {
    if (!result) return null;
    switch (result.status) {
      case 'success':
        return null;
      case 'pending':
        return 'Checking property databases…';
      case 'incomplete_address':
        return 'Exact house address not found — please refine';
      case 'sources_blocked':
        return 'Property sites blocked our request';
      case 'sources_timeout':
        return 'Property sites didn\'t respond in time';
      case 'low_confidence':
        return 'No reliable match found';
      case 'not_found':
        return 'Property details not available';
      default:
        return 'Property details not available';
    }
  };
  
  // Detailed failure explanation (human-readable, for the card subtitle)
  const getStatusDetail = () => {
    if (!result) return '';
    if (result.status === 'pending') return 'Searching…';
    if (result.status === 'incomplete_address') return 'You entered a house number but only a street-level match was found.';
    const attempts = result.source_attempts || {};
    const attemptKeys = Object.keys(attempts);
    if (attemptKeys.length > 0) {
      const summary = attemptKeys.map(src => {
        const st = attempts[src];
        if (st === 'ok') return `${src}: matched`;
        if (st && st.startsWith('blocked_')) return `${src}: blocked (${st.split('_')[1]})`;
        if (st === 'timeout') return `${src}: timed out`;
        if (st === 'not_found_404') return `${src}: no listing`;
        if (st === 'parse_failed') return `${src}: page changed`;
        return `${src}: ${st}`;
      }).join(' · ');
      return summary;
    }
    return 'Use the manual search links below.';
  };
  
  return (
    <div className="container mx-auto px-4 py-6 max-w-2xl">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Home className="h-5 w-5" />
              Property Lookup
            </CardTitle>
            <span className="text-xs text-zinc-400 bg-zinc-100 px-2 py-1 rounded">
              Internal best-effort lookup
            </span>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Search Form with Autocomplete */}
          <form onSubmit={handleSubmit} className="relative">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  ref={inputRef}
                  value={query}
                  onChange={handleInputChange}
                  onFocus={() => query.length >= 3 && suggestions.length > 0 && setShowSuggestions(true)}
                  placeholder="Start typing an address..."
                  className="flex-1 pr-8"
                  disabled={loading}
                  autoComplete="off"
                  data-testid="property-address-input"
                />
                {loadingSuggestions && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-zinc-400" />
                )}
              </div>
              <Button 
                type="submit" 
                disabled={loading || !query.trim()} 
                data-testid="property-lookup-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
            
            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div 
                ref={dropdownRef}
                className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg overflow-hidden"
              >
                <div className="text-xs text-zinc-400 px-3 py-2 border-b border-zinc-100 bg-zinc-50">
                  Select an address
                </div>
                {suggestions.map((suggestion, index) => {
                  const isExact = suggestion.is_exact === true;
                  return (
                    <button
                      key={index}
                      type="button"
                      className={`w-full px-3 py-2.5 text-left flex items-start gap-2 border-b border-zinc-50 last:border-0 transition-colors ${
                        isExact ? 'hover:bg-amber-50' : 'hover:bg-zinc-50 bg-zinc-50/40'
                      }`}
                      onClick={() => handleSelectSuggestion(suggestion)}
                      data-testid={`suggestion-${index}`}
                      data-exact={isExact ? 'true' : 'false'}
                    >
                      <MapPin className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isExact ? 'text-amber-500' : 'text-zinc-300'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className={`text-sm ${isExact ? 'font-medium text-zinc-900' : 'text-zinc-600'}`}>
                            {suggestion.display}
                          </div>
                          {!isExact && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600">
                              Road only
                            </span>
                          )}
                        </div>
                        {suggestion.postcode && (
                          <div className="text-xs text-zinc-500">
                            {suggestion.suburb}, {suggestion.state} {suggestion.postcode}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </form>
          
          {/* Empty State */}
          {!loading && !result && !query && (
            <div className="text-center py-8 text-zinc-400">
              <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Start typing an address to search</p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-8 text-zinc-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              <p className="text-sm">Looking up property details...</p>
            </div>
          )}

          {/* Error/Not Found State — still show normalized address + manual search links */}
          {result && result.status !== 'success' && !loading && (
            <div className="border border-zinc-200 rounded-lg overflow-hidden" data-testid="property-not-found-card">
              <div className="bg-amber-50 border-b border-amber-100 px-4 py-3 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-900">{getStatusMessage()}</p>
                  <p className="text-xs text-amber-700 mt-0.5" data-testid="status-detail">
                    {getStatusDetail()}
                  </p>
                </div>
              </div>

              <div className="p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-500">Address</p>
                    <p className="text-sm font-medium text-zinc-900 break-words" data-testid="not-found-address">
                      {result.matched_address || result.address_input}
                    </p>
                  </div>
                </div>

                {(result.domain_search_url || result.realestate_search_url || result.google_search_url) && (
                  <div className="pt-2 border-t border-zinc-100">
                    <p className="text-xs text-zinc-500 mb-2">Search manually:</p>
                    <div className="flex flex-wrap gap-2">
                      {result.domain_search_url && (
                        <a
                          href={result.domain_search_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 rounded-full inline-flex items-center gap-1 transition-colors"
                          data-testid="fallback-domain-link"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Search on Domain
                        </a>
                      )}
                      {result.realestate_search_url && (
                        <a
                          href={result.realestate_search_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 rounded-full inline-flex items-center gap-1 transition-colors"
                          data-testid="fallback-realestate-link"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Search on realestate.com.au
                        </a>
                      )}
                      {result.google_search_url && (
                        <a
                          href={result.google_search_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 rounded-full inline-flex items-center gap-1 transition-colors"
                          data-testid="fallback-google-link"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Google
                        </a>
                      )}
                    </div>
                  </div>
                )}

                <div className="pt-2 flex items-center justify-end border-t border-zinc-100">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(result.matched_address || result.address_input);
                        setCopied(true);
                        toast.success('Address copied');
                        setTimeout(() => setCopied(false), 2000);
                      } catch (e) {
                        toast.error('Could not copy — check browser permissions');
                      }
                    }}
                    className="h-8"
                    data-testid="copy-address-btn"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3.5 w-3.5 mr-1.5 text-green-600" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5 mr-1.5" />
                        Copy address
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Success Result */}
          {result && result.status === 'success' && !loading && (
            <div className="border border-zinc-200 rounded-lg overflow-hidden">
              {/* Cached indicator */}
              {result.cached && (
                <div className="bg-green-50 text-green-700 text-xs px-3 py-1 border-b border-green-100">
                  Cached result (instant)
                </div>
              )}
              
              {/* Image */}
              {result.image_url ? (
                <div className="h-48 bg-zinc-100 overflow-hidden">
                  <img
                    src={result.image_url}
                    alt="Property"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden h-full items-center justify-center text-zinc-400">
                    <ImageOff className="h-8 w-8" />
                  </div>
                </div>
              ) : (
                <div className="h-32 bg-zinc-100 flex items-center justify-center text-zinc-400">
                  <div className="text-center">
                    <ImageOff className="h-8 w-8 mx-auto mb-1" />
                    <p className="text-xs">No image available</p>
                  </div>
                </div>
              )}

              {/* Details */}
              <div className="p-4 space-y-3">
                {/* Address */}
                <div>
                  <p className="font-medium text-zinc-900">
                    {result.matched_address || result.address_input}
                  </p>
                  {result.confidence && (
                    <p className="text-xs text-zinc-400 mt-0.5">
                      Match confidence: {result.confidence}
                    </p>
                  )}
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-3 gap-3 pt-2">
                  <div className="flex items-center gap-2 text-zinc-700">
                    <Bed className="h-4 w-4 text-zinc-400" />
                    <span className="text-sm font-medium">
                      {result.bedrooms ?? 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-700">
                    <Bath className="h-4 w-4 text-zinc-400" />
                    <span className="text-sm font-medium">
                      {result.bathrooms ?? 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-700">
                    <Ruler className="h-4 w-4 text-zinc-400" />
                    <span className="text-sm font-medium">
                      {result.house_size ? `${result.house_size} ${result.house_size_unit || 'm²'}` : 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Source Link & Copy */}
                <div className="pt-2 flex items-center justify-between border-t border-zinc-100">
                  {result.source_url ? (
                    <a
                      href={result.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {result.source_name}
                    </a>
                  ) : (
                    <span className="text-xs text-zinc-400">No source link</span>
                  )}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyToClipboard}
                    className="h-8"
                    data-testid="copy-result-btn"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3.5 w-3.5 mr-1.5 text-green-600" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5 mr-1.5" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
